import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

const DEFAULT_SLIDES = [
  {
    id: 1,
    badge: "● SERVERE LIVE CS 1.6",
    title: "SERVERE COMPETITIVE DE CS 1.6",
    subtitle: "Classic Dust2, Cobble War & Inferno cu IP-uri active și conectare instant.",
    link: "/servere",
    btnText: "Conectează-te pe Servere",
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=1200&auto=format&fit=crop"
  },
  {
    id: 2,
    badge: "💬 DISCORD INDUNGI NETWORK",
    title: "ALĂTURĂ-TE PE SERVERUL DISCORD",
    subtitle: "532+ Membri activi în comunitate. Meciuri de mix, discuții și canale voice.",
    link: "https://discord.gg/CrGC77X8W",
    isExternal: true,
    btnText: "Intră pe Discord",
    image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=1200&auto=format&fit=crop"
  },
  {
    id: 3,
    badge: "🏆 TOP CONTRIBUTORI & RECOMPENSE",
    title: "URCĂ ÎN TOPUL LUNAR AL FORUMULUI",
    subtitle: "Postează, primește reacții de la comunitate și câștigă statutul VIP automat.",
    link: "/leaderboard",
    btnText: "Vezi Clasamentul",
    image: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?q=80&w=1200&auto=format&fit=crop"
  },
  {
    id: 4,
    badge: "👑 BENEFICII EXCLUSIVE",
    title: "DEBLOCHEAZĂ STATUTUL VIP",
    subtitle: "Rame animate GIF pentru avatar, culori speciale pe forum și acces la pachete VIP.",
    link: "/vip",
    btnText: "Explorează Pachetele VIP",
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=1200&auto=format&fit=crop"
  }
];

export default function HeroSlider({ slides = [] }) {
  const activeSlides = slides && slides.length > 0 ? slides : DEFAULT_SLIDES;

  if (!activeSlides.length) return null;

  return (
    <section className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a0c10] shadow-2xl min-h-[250px] h-[300px] sm:h-60 lg:h-72 select-none group image-slider" data-testid="hero-slider">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        effect="fade"
        speed={700}
        loop={true}
        grabCursor={true}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          renderBullet: function (index, className) {
            return `<span class="${className} w-2 h-1.5 rounded-full bg-white/20 hover:bg-white/40 transition-all duration-300"></span>`;
          },
        }}
        navigation={{
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        }}
        className="h-full w-full"
      >
        {activeSlides.map((s, idx) => (
          <SwiperSlide key={s.id || idx}>
            <div className="absolute inset-0 w-full h-full">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-[10000ms] scale-100 hover:scale-110" 
                style={{ backgroundImage: `url(${s.image || s.image_url})` }} 
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c10] via-transparent to-black/40" />
            </div>

            <div className="relative z-10 h-full flex flex-col justify-center px-6 lg:px-10 max-w-3xl pointer-events-none">
              <div className="inline-flex items-center gap-1.5 w-fit px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur border border-white/15 text-[10px] font-mono font-bold text-[var(--tf-primary)] uppercase tracking-wider mb-2 shadow-md">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--tf-primary)] animate-pulse" />
                {s.badge || "// INDUNGI ROMANIA"}
              </div>

              <h2 className="font-['Orbitron'] text-xl lg:text-3xl font-extrabold uppercase tracking-tight text-white line-clamp-1 drop-shadow-md">
                {s.title}
              </h2>

              <p className="text-zinc-300 text-xs lg:text-sm mt-1.5 line-clamp-2 max-w-xl leading-relaxed">
                {s.subtitle}
              </p>

              {s.link && (
                <div className="mt-3.5 pointer-events-auto">
                  {s.isExternal ? (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noreferrer"
                      className="py-1.5 px-4 bg-[var(--tf-primary)] hover:bg-orange-600 text-white font-mono font-bold text-xs uppercase tracking-wider rounded-lg shadow-md hover:shadow-[0_0_15px_rgba(255,79,0,0.5)] transition-all inline-flex items-center gap-1.5"
                    >
                      {s.btnText || "Explorează"} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={s.link}
                      className="py-1.5 px-4 bg-[var(--tf-primary)] hover:bg-orange-600 text-white font-mono font-bold text-xs uppercase tracking-wider rounded-lg shadow-md hover:shadow-[0_0_15px_rgba(255,79,0,0.5)] transition-all inline-flex items-center gap-1.5"
                      data-testid={`slide-cta-${s.id}`}
                    >
                      {s.btnText || "Explorează"} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}

        <div className="swiper-button-prev-custom absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-lg bg-black/50 hover:bg-black/80 border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </div>
        
        <div className="swiper-button-next-custom absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-lg bg-black/50 hover:bg-black/80 border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </div>
      </Swiper>
      
      <style>{`
        .swiper-pagination-bullet-active {
          width: 28px !important;
          background: var(--tf-primary) !important;
          box-shadow: 0 0 8px rgba(255, 79, 0, 0.6);
        }
        .swiper-pagination-bullet {
          transition: all 0.3s ease;
        }
        .swiper-pagination-bullets {
          bottom: 12px !important;
          right: 24px !important;
          left: auto !important;
          width: auto !important;
          display: flex;
          align-items: center;
          gap: 6px;
          z-index: 20;
        }
      `}</style>
    </section>
  );
}
